/**
 * Super Admin ROI Dashboard (View 2)
 *
 * Comprehensive ecosystem ROI dashboard for super admins showing:
 * - MCP perspective (same as View 1)
 * - Trust-WeaveIn platform revenue and costs
 * - Platform profit margin
 * - Combined ecosystem ROI
 * - Network metrics (CBOs, members, actions)
 * - Trust pricing configuration and profitability modeling
 * - Baseline threshold pricing
 * - Stripe cost breakdown with chargeback analysis
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getWeaveinApiUrl } from '@/src/config';
import MCPROIDashboard from '../sponsors/MCPROIDashboard';
import ROIModeler from '../sponsors/ROIModeler';
import { Settings, Calculator, CreditCard, TrendingUp, AlertCircle, Sliders, Target } from 'lucide-react';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
// Pricing configuration interface
interface PricingConfig {
  transactionFeePercent: number;  // e.g., 2.5% (max 50%)
  setupFee: number;               // One-time setup fee
  subscriptionFeeMonthly: number; // Monthly subscription
  analyticsFeePercent: number;    // % of payout volume
  baselineEnrollmentPercent: number;  // Sponsor's baseline without Trust (e.g., 20%)
  targetEnrollmentPercent: number;    // Target with Trust (e.g., 30%)
  // Payment processing configuration (Stripe, Adyen, Marqeta, etc.)
  paymentProcessingFeePercent: number;  // e.g., 2.9%
  perTransactionFee: number;            // e.g., $0.30
  chargebackPercent: number;            // % chargeable to sponsor (e.g., 80%)
  // Infrastructure & Operations costs (attributable to program)
  cloudHostingCosts: number;           // Cloud hosting per program
  databaseStorageCosts: number;        // Database & storage costs
  personnelCosts: number;              // Personnel allocation
  supportToolsCosts: number;           // Support tools & systems
  complianceSecurityCosts: number;     // Compliance & security
  marketingSalesCosts: number;         // Marketing & sales allocation
}

// Payment processing cost breakdown interface (generic - works with Stripe, Adyen, Marqeta, etc.)
interface PaymentProcessingBreakdown {
  totalVolume: number;
  processingFeePercent: number;     // e.g., 2.9%
  perTransactionFee: number;        // e.g., $0.30
  transactionCount: number;
  estimatedProcessingFees: number;
  chargeableToSponsor: number;      // Amount we can pass through
  platformAbsorbed: number;         // Amount platform absorbs
}

interface PlatformROIActuals {
  setup_fee_revenue: number;
  subscription_revenue: number;
  transaction_fee_revenue: number;
  analytics_fee_revenue: number;
  integration_fee_revenue: number;
  total_platform_revenue: number;
  total_transactions_processed: number;
  total_payout_volume: number;
  avg_transaction_size: number;
  cloud_hosting_costs: number;
  database_storage_costs: number;
  stripe_processing_fees: number;
  personnel_costs: number;
  support_tools_costs: number;
  compliance_security_costs: number;
  marketing_sales_costs: number;
  total_platform_costs: number;
  cbos_onboarded: number;
  members_enrolled: number;
  actions_tracked: number;
  data_points_captured: number;
  platform_net_profit: number;
  platform_profit_margin: number;
  mcp_net_benefit: number;
  total_ecosystem_value: number;
  total_ecosystem_investment: number;
  ecosystem_roi_percentage: number;
  ecosystem_roi_multiple: number;
  as_of_date: string;
  fiscal_year: number;
  fiscal_quarter: number;
}

interface EcosystemSummary {
  mcp_net_benefit: number;
  mcp_investment: number;
  mcp_roi_multiple: number;
  platform_net_profit: number;
  platform_investment: number;
  platform_profit_margin: number;
  total_ecosystem_value: number;
  total_ecosystem_investment: number;
  ecosystem_roi_percentage: number;
  ecosystem_roi_multiple: number;
  platform_revenue: number;
  cbos_onboarded: number;
  members_enrolled: number;
  actions_tracked: number;
}

interface SuperAdminROIData {
  program_id: number;
  program_name: string;
  sponsor_id: number;
  sponsor_name: string;
  mcp_roi: any; // Contains full MCP ROI view
  platform_actuals: PlatformROIActuals;
  ecosystem_summary: EcosystemSummary;
  as_of_date: string;
}

interface SuperAdminROIDashboardProps {
  programId: number;
}

const SuperAdminROIDashboard: React.FC<SuperAdminROIDashboardProps> = ({ programId }) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [data, setData] = useState<SuperAdminROIData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'ecosystem' | 'mcp' | 'platform' | 'pricing' | 'modeler'>('ecosystem');

  // Program selector state
  const [programs, setPrograms] = useState<any[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<number>(programId);

  // Pricing configuration state
  const [pricingConfig, setPricingConfig] = useState<PricingConfig>({
    transactionFeePercent: 2.5,
    setupFee: 10000,
    subscriptionFeeMonthly: 2500,
    analyticsFeePercent: 0.5,
    baselineEnrollmentPercent: 20,
    targetEnrollmentPercent: 30,
    // Payment processing defaults (can be adjusted for Stripe, Adyen, Marqeta, etc.)
    paymentProcessingFeePercent: 2.9,
    perTransactionFee: 0.30,
    chargebackPercent: 80,  // 80% chargeable to sponsor by default
    // Infrastructure & Operations costs (attributable to program)
    cloudHostingCosts: 2500,        // Monthly cloud hosting
    databaseStorageCosts: 1000,     // Monthly database/storage
    personnelCosts: 15000,          // Monthly personnel allocation
    supportToolsCosts: 500,         // Monthly support tools
    complianceSecurityCosts: 2000,  // Monthly compliance/security
    marketingSalesCosts: 3000       // Monthly marketing/sales
  });

  // Fetch programs for selector - filtered by sponsor/CBO for data isolation
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const cboId = localStorage.getItem('active_cbo_id') || '6';
        const sponsorId = localStorage.getItem('active_sponsor_id');

        // Build query params for data isolation
        let queryParams = `cbo_id=${cboId}`;
        if (sponsorId) {
          queryParams += `&sponsor_id=${sponsorId}`;
        }

        const response = await axios.get(
          getWeaveinApiUrl(`/api/programs?${queryParams}`),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-CBO-ID': cboId
            }
          }
        );

        // Filter to only show programs belonging to this organization
        const filteredPrograms = response.data.filter((program: any) => {
          // Only show programs that match the current CBO/sponsor
          return program.cbo_id === parseInt(cboId) ||
                 (sponsorId && program.sponsor_id === parseInt(sponsorId));
        });

        setPrograms(filteredPrograms);

        // If no matching program, clear selection
        if (filteredPrograms.length > 0 && !filteredPrograms.find((p: any) => p.id === selectedProgramId)) {
          setSelectedProgramId(filteredPrograms[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch programs:', err);
      }
    };
    fetchPrograms();
  }, []);

  useEffect(() => {
    fetchROIData();
  }, [selectedProgramId]);

  // Calculate payment processing breakdown (works with Stripe, Adyen, Marqeta, etc.)
  const calculatePaymentProcessingBreakdown = (): PaymentProcessingBreakdown => {
    if (!data) {
      return {
        totalVolume: 0,
        processingFeePercent: pricingConfig.paymentProcessingFeePercent,
        perTransactionFee: pricingConfig.perTransactionFee,
        transactionCount: 0,
        estimatedProcessingFees: 0,
        chargeableToSponsor: 0,
        platformAbsorbed: 0
      };
    }

    const { platform_actuals } = data;
    const totalVolume = platform_actuals.total_payout_volume;
    const transactionCount = platform_actuals.total_transactions_processed;

    // Payment processing fees based on configurable rates
    const percentageFees = totalVolume * (pricingConfig.paymentProcessingFeePercent / 100);
    const perTransactionFees = transactionCount * pricingConfig.perTransactionFee;
    const totalProcessingFees = percentageFees + perTransactionFees;

    // Chargeback based on configurable percentage
    const chargeableToSponsor = totalProcessingFees * (pricingConfig.chargebackPercent / 100);
    const platformAbsorbed = totalProcessingFees * ((100 - pricingConfig.chargebackPercent) / 100);

    return {
      totalVolume,
      processingFeePercent: pricingConfig.paymentProcessingFeePercent,
      perTransactionFee: pricingConfig.perTransactionFee,
      transactionCount,
      estimatedProcessingFees: totalProcessingFees,
      chargeableToSponsor,
      platformAbsorbed
    };
  };

  // Calculate pricing impact based on baseline
  const calculatePricingImpact = () => {
    if (!data) return null;

    const { platform_actuals } = data;
    const totalEnrollments = platform_actuals.members_enrolled;

    // Calculate enrollments attributable to Trust (above baseline)
    const baselineEnrollments = totalEnrollments * (pricingConfig.baselineEnrollmentPercent / pricingConfig.targetEnrollmentPercent);
    const trustAttributableEnrollments = totalEnrollments - baselineEnrollments;

    // Revenue calculation based only on Trust-attributable value
    const incrementalValue = (trustAttributableEnrollments / totalEnrollments) * platform_actuals.total_payout_volume;
    const trustFeeRevenue = incrementalValue * (pricingConfig.transactionFeePercent / 100);

    return {
      totalEnrollments,
      baselineEnrollments: Math.round(baselineEnrollments),
      trustAttributableEnrollments: Math.round(trustAttributableEnrollments),
      incrementalValue,
      trustFeeRevenue,
      improvementPercent: pricingConfig.targetEnrollmentPercent - pricingConfig.baselineEnrollmentPercent
    };
  };

  const fetchROIData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(
        getWeaveinApiUrl(`/api/roi/programs/${selectedProgramId}/superadmin-view`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || ''
          }
        }
      );
      setData(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load ROI data');
      console.error('Error fetching ROI data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number): string => {
    return `${(value ?? 0).toFixed(1)}%`;
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const getROIBadgeColor = (roiMultiple: number): string => {
    if (roiMultiple >= 5) return 'bg-green-100 text-green-800';
    if (roiMultiple >= 3) return 'bg-blue-100 text-blue-800';
    if (roiMultiple >= 1) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getProfitMarginColor = (margin: number): string => {
    if (margin >= 20) return 'text-green-600';
    if (margin >= 10) return 'text-blue-600';
    if (margin >= 0) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-gray-700 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-300">{error}</p>
        <button
          onClick={fetchROIData}
          className="mt-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 underline"
        >
          {t('admin.retry', 'Retry')}
        </button>
      </div>
    );
  }

  if (!data) {
    return <div>{t('admin.no_roi_data_available', 'No ROI data available')}</div>;
  }

  const { ecosystem_summary, platform_actuals } = data;

  const processingBreakdown = calculatePaymentProcessingBreakdown();
  const pricingImpact = calculatePricingImpact();

  return (
    <div className="space-y-6">
      {/* Program Selector */}
      {programs.length > 1 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('admin.select_program', 'Select Program:')}
            </label>
            <select
              value={selectedProgramId}
              onChange={(e) => setSelectedProgramId(parseInt(e.target.value))}
              className="flex-1 max-w-md px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              {programs.map((program) => (
                <option key={program.id} value={program.id}>
                  {program.name} ({program.program_type})
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg rounded-lg p-6">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center space-x-2">
              <span className="inline-block px-3 py-1 bg-white bg-opacity-20 rounded-full text-xs font-semibold">
                SUPER ADMIN VIEW
              </span>
            </div>
            <h1 className="text-3xl font-bold mt-2">{data.program_name}</h1>
            <p className="text-purple-100 mt-1">{data.sponsor_name}</p>
            <p className="text-sm text-purple-200 mt-1">
              As of {fmt.short(data.as_of_date)} · FY{platform_actuals.fiscal_year} Q{platform_actuals.fiscal_quarter}
            </p>
          </div>
          <div className="text-right">
            <div className="bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 px-6 py-3 rounded-lg shadow-md">
              <p className="text-xs font-semibold uppercase tracking-wide">{t('admin.ecosystem_roi', 'Ecosystem ROI')}</p>
              <p className="text-4xl font-bold mt-1">
                {(ecosystem_summary?.ecosystem_roi_multiple ?? 0).toFixed(1)}x
              </p>
              <p className="text-sm mt-1">
                {formatPercent(ecosystem_summary.ecosystem_roi_percentage)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('ecosystem')}
              className={`px-6 py-3 border-b-2 font-medium text-sm ${
                activeTab === 'ecosystem'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {t('admin.ecosystem_overview', 'Ecosystem Overview')}
            </button>
            <button
              onClick={() => setActiveTab('platform')}
              className={`px-6 py-3 border-b-2 font-medium text-sm ${
                activeTab === 'platform'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {t('admin.platform_performance', 'Platform Performance')}
            </button>
            <button
              onClick={() => setActiveTab('pricing')}
              className={`px-6 py-3 border-b-2 font-medium text-sm ${
                activeTab === 'pricing'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Sliders className="w-4 h-4" />
                <span>{t('admin.pricing_model', 'Pricing Model')}</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('modeler')}
              className={`px-6 py-3 border-b-2 font-medium text-sm ${
                activeTab === 'modeler'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Target className="w-4 h-4" />
                <span>{t('admin.roi_modeler', 'ROI Modeler')}</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('mcp')}
              className={`px-6 py-3 border-b-2 font-medium text-sm ${
                activeTab === 'mcp'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {t('admin.mcp_sponsor_view', 'MCP Sponsor View')}
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'ecosystem' && (
            <div className="space-y-6">
              {/* Ecosystem Value Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* MCP Value */}
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-gray-700 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-300 uppercase">{t('admin.mcp_value', 'MCP Value')}</h3>
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${getROIBadgeColor(ecosystem_summary.mcp_roi_multiple)}`}>
                      {(ecosystem_summary?.mcp_roi_multiple ?? 0).toFixed(1)}x
                    </span>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-blue-600 dark:text-blue-400">{t('admin.net_benefit', 'Net Benefit')}</p>
                      <p className="text-2xl font-bold text-blue-900 dark:text-blue-300">
                        {formatCurrency(ecosystem_summary.mcp_net_benefit)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-600 dark:text-blue-400">{t('admin.investment', 'Investment')}</p>
                      <p className="text-lg text-blue-700 dark:text-blue-400">
                        {formatCurrency(ecosystem_summary.mcp_investment)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Platform Value */}
                <div className="bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-gray-700 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-purple-900 dark:text-purple-300 uppercase">{t('admin.platform_profit', 'Platform Profit')}</h3>
                    <span className={`text-xs font-semibold ${getProfitMarginColor(ecosystem_summary.platform_profit_margin)}`}>
                      {formatPercent(ecosystem_summary.platform_profit_margin)} margin
                    </span>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-purple-600 dark:text-purple-400">{t('admin.net_profit_actual', 'Net Profit (Actual)')}</p>
                      <p className="text-2xl font-bold text-purple-900 dark:text-purple-300">
                        {formatCurrency(ecosystem_summary.platform_net_profit)}
                      </p>
                    </div>
                    {pricingImpact && (
                      <div className="bg-purple-100 dark:bg-purple-900/40 rounded p-2">
                        <p className="text-xs text-purple-600 dark:text-purple-400">{t('admin.modeled_profit', 'Modeled Profit')}</p>
                        <p className="text-lg font-bold text-purple-700 dark:text-purple-300">
                          {formatCurrency(
                            pricingConfig.setupFee +
                            (pricingConfig.subscriptionFeeMonthly * 12) +
                            (pricingImpact.trustFeeRevenue || 0) -
                            processingBreakdown.platformAbsorbed
                          )}
                        </p>
                        <p className="text-xs text-purple-500 dark:text-purple-400 mt-1">
                          {t('admin.based_on_pricing_config', 'Based on pricing config')}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-purple-600 dark:text-purple-400">{t('admin.investment_1', 'Investment')}</p>
                      <p className="text-lg text-purple-700 dark:text-purple-400">
                        {formatCurrency(ecosystem_summary.platform_investment)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Total Ecosystem */}
                <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-gray-700 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-green-900 dark:text-green-300 uppercase">{t('admin.total_ecosystem', 'Total Ecosystem')}</h3>
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${getROIBadgeColor(ecosystem_summary.ecosystem_roi_multiple)}`}>
                      {(ecosystem_summary?.ecosystem_roi_multiple ?? 0).toFixed(1)}x
                    </span>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-green-600 dark:text-green-400">{t('admin.total_value', 'Total Value')}</p>
                      <p className="text-2xl font-bold text-green-900 dark:text-green-300">
                        {formatCurrency(ecosystem_summary.total_ecosystem_value)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-green-600 dark:text-green-400">{t('admin.total_investment', 'Total Investment')}</p>
                      <p className="text-lg text-green-700 dark:text-green-400">
                        {formatCurrency(ecosystem_summary.total_ecosystem_investment)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Network Metrics */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{t('admin.network_metrics', 'Network Metrics')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('admin.cbos_onboarded', 'CBOs Onboarded')}</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                      {formatNumber(ecosystem_summary.cbos_onboarded)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('admin.members_enrolled', 'Members Enrolled')}</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                      {formatNumber(ecosystem_summary.members_enrolled)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('admin.actions_tracked', 'Actions Tracked')}</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                      {formatNumber(ecosystem_summary.actions_tracked)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('admin.data_points', 'Data Points')}</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                      {formatNumber(platform_actuals.data_points_captured)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Visual Breakdown */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{t('admin.value_distribution', 'Value Distribution')}</h3>
                <div className="relative pt-1">
                  <div className="flex mb-2 items-center justify-between">
                    <div>
                      <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-blue-600 bg-blue-200">
                        {t('admin.mcp_benefit', 'MCP Benefit')}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-semibold inline-block text-blue-600">
                        {formatCurrency(ecosystem_summary.mcp_net_benefit)}
                      </span>
                    </div>
                  </div>
                  <div className="overflow-hidden h-8 mb-4 text-xs flex rounded bg-blue-200">
                    <div
                      style={{ width: `${(ecosystem_summary.mcp_net_benefit / ecosystem_summary.total_ecosystem_value) * 100}%` }}
                      className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"
                    >
                      {formatPercent((ecosystem_summary.mcp_net_benefit / ecosystem_summary.total_ecosystem_value) * 100)}
                    </div>
                  </div>

                  <div className="flex mb-2 items-center justify-between">
                    <div>
                      <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-purple-600 bg-purple-200">
                        {t('admin.platform_profit_1', 'Platform Profit')}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-semibold inline-block text-purple-600">
                        {formatCurrency(ecosystem_summary.platform_net_profit)}
                      </span>
                    </div>
                  </div>
                  <div className="overflow-hidden h-8 mb-4 text-xs flex rounded bg-purple-200">
                    <div
                      style={{ width: `${(ecosystem_summary.platform_net_profit / ecosystem_summary.total_ecosystem_value) * 100}%` }}
                      className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-purple-500"
                    >
                      {formatPercent((ecosystem_summary.platform_net_profit / ecosystem_summary.total_ecosystem_value) * 100)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'platform' && (
            <div className="space-y-6">
              {/* Platform Revenue Breakdown */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{t('admin.platform_revenue_streams', 'Platform Revenue Streams')}</h3>
                <div className="space-y-4">
                  {[
                    {
                      label: 'Setup Fee',
                      actual: platform_actuals.setup_fee_revenue,
                      modeled: pricingConfig.setupFee
                    },
                    {
                      label: 'Subscription Revenue (Annual)',
                      actual: platform_actuals.subscription_revenue,
                      modeled: pricingConfig.subscriptionFeeMonthly * 12
                    },
                    {
                      label: 'Transaction Fees',
                      actual: platform_actuals.transaction_fee_revenue,
                      modeled: pricingImpact?.trustFeeRevenue || 0
                    },
                    {
                      label: 'Analytics & Reporting',
                      actual: platform_actuals.analytics_fee_revenue,
                      modeled: platform_actuals.total_payout_volume * (pricingConfig.analyticsFeePercent / 100)
                    },
                    {
                      label: 'Integration & Support',
                      actual: platform_actuals.integration_fee_revenue,
                      modeled: platform_actuals.integration_fee_revenue // Keep same as actual
                    },
                  ].map((item, idx) => {
                    const totalActual = platform_actuals.total_platform_revenue;
                    const totalModeled = pricingConfig.setupFee +
                      (pricingConfig.subscriptionFeeMonthly * 12) +
                      (pricingImpact?.trustFeeRevenue || 0) +
                      (platform_actuals.total_payout_volume * (pricingConfig.analyticsFeePercent / 100)) +
                      platform_actuals.integration_fee_revenue;

                    return (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.label}</span>
                            <div className="text-right">
                              <span className="text-sm font-bold text-gray-900 dark:text-white">{formatCurrency(item.actual)}</span>
                              {item.modeled !== item.actual && (
                                <span className="text-xs text-purple-600 ml-2">
                                  (Model: {formatCurrency(item.modeled)})
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 relative">
                            <div
                              className="bg-purple-500 h-2 rounded-full"
                              style={{ width: `${(item.actual / totalActual) * 100}%` }}
                            ></div>
                            {item.modeled !== item.actual && (
                              <div
                                className="absolute top-0 h-2 border-l-2 border-purple-300"
                                style={{ left: `${(item.modeled / totalModeled) * 100}%` }}
                              ></div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-base font-bold text-gray-900 dark:text-white">Total Revenue (Actual)</span>
                      <span className="text-xl font-bold text-purple-600">
                        {formatCurrency(platform_actuals.total_platform_revenue)}
                      </span>
                    </div>
                    {pricingImpact && (
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Revenue (Modeled)</span>
                        <span className="text-lg font-bold text-purple-500">
                          {formatCurrency(
                            pricingConfig.setupFee +
                            (pricingConfig.subscriptionFeeMonthly * 12) +
                            (pricingImpact.trustFeeRevenue || 0) +
                            (platform_actuals.total_payout_volume * (pricingConfig.analyticsFeePercent / 100)) +
                            platform_actuals.integration_fee_revenue
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Platform Cost Breakdown */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{t('admin.platform_cost_structure', 'Platform Cost Structure')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase">{t('admin.infrastructure', 'Infrastructure')}</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('admin.cloud_hosting', 'Cloud Hosting')}</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(platform_actuals.cloud_hosting_costs)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('admin.database_storage', 'Database & Storage')}</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(platform_actuals.database_storage_costs)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {t('admin.payment_processing', 'Payment Processing')}
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                            ({pricingConfig.chargebackPercent}% absorbed)
                          </span>
                        </span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(processingBreakdown.platformAbsorbed)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500 dark:text-gray-400 pl-2">{t('admin.full_processing_fees', 'Full processing fees')}</span>
                        <span className="text-gray-500 dark:text-gray-400">
                          {formatCurrency(platform_actuals.stripe_processing_fees)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase">{t('admin.operations', 'Operations')}</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('admin.personnel', 'Personnel')}</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(platform_actuals.personnel_costs)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('admin.support_tools', 'Support Tools')}</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(platform_actuals.support_tools_costs)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('admin.compliance_security', 'Compliance & Security')}</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(platform_actuals.compliance_security_costs)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('admin.marketing_sales', 'Marketing & Sales')}</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(platform_actuals.marketing_sales_costs)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-base font-bold text-gray-900 dark:text-white">
                      {t('admin.total_costs', 'Total Costs')}
                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">(with chargeback)</span>
                    </span>
                    <span className="text-xl font-bold text-red-600">
                      {formatCurrency(
                        platform_actuals.total_platform_costs -
                        platform_actuals.stripe_processing_fees +
                        processingBreakdown.platformAbsorbed
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Full costs (without chargeback)</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {formatCurrency(platform_actuals.total_platform_costs)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Platform Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                  <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase mb-4">{t('admin.transaction_metrics', 'Transaction Metrics')}</h4>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{t('admin.total_transactions', 'Total Transactions')}</p>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                        {formatNumber(platform_actuals.total_transactions_processed)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{t('admin.total_payout_volume', 'Total Payout Volume')}</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                        {formatCurrency(platform_actuals.total_payout_volume)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{t('admin.average_transaction_size', 'Average Transaction Size')}</p>
                      <p className="text-xl font-semibold text-gray-700 dark:text-gray-300 mt-1">
                        {formatCurrency(platform_actuals.avg_transaction_size)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                  <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase mb-4">{t('admin.profitability', 'Profitability')}</h4>
                  {(() => {
                    // Calculate adjusted profit with chargeback approach
                    const adjustedCosts = platform_actuals.total_platform_costs -
                      platform_actuals.stripe_processing_fees +
                      processingBreakdown.platformAbsorbed;
                    const adjustedNetProfit = platform_actuals.total_platform_revenue - adjustedCosts;
                    const adjustedProfitMargin = platform_actuals.total_platform_revenue > 0
                      ? (adjustedNetProfit / platform_actuals.total_platform_revenue) * 100
                      : 0;

                    return (
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {t('admin.net_profit_actual_1', 'Net Profit (Actual)')}
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">(with chargeback)</span>
                          </p>
                          <p className={`text-3xl font-bold mt-1 ${getProfitMarginColor(adjustedProfitMargin)}`}>
                            {formatCurrency(adjustedNetProfit)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Without chargeback: {formatCurrency(platform_actuals.platform_net_profit)}
                          </p>
                        </div>
                        {pricingImpact && (
                          <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-3">
                            <p className="text-sm text-purple-600 dark:text-purple-400">{t('admin.modeled_net_profit', 'Modeled Net Profit')}</p>
                            <p className="text-2xl font-bold text-purple-700 mt-1">
                              {formatCurrency(
                                pricingConfig.setupFee +
                                (pricingConfig.subscriptionFeeMonthly * 12) +
                                (pricingImpact.trustFeeRevenue || 0) -
                                processingBreakdown.platformAbsorbed
                              )}
                            </p>
                            <p className="text-xs text-purple-500 dark:text-purple-400 mt-1">
                              {t('admin.based_on_pricing_configuration', 'Based on pricing configuration')}
                            </p>
                          </div>
                        )}
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Profit Margin (Actual)</p>
                          <p className={`text-2xl font-bold mt-1 ${getProfitMarginColor(adjustedProfitMargin)}`}>
                            {formatPercent(adjustedProfitMargin)}
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'mcp' && (
            <div>
              <MCPROIDashboard programId={selectedProgramId} sponsorId={data.sponsor_id} />
            </div>
          )}

          {activeTab === 'modeler' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 border border-indigo-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-bold text-indigo-900 dark:text-indigo-300 mb-2">{t('admin.platform_roi_scenario_modeler', 'Platform ROI Scenario Modeler')}</h3>
                <p className="text-sm text-indigo-700 dark:text-indigo-300">
                  Create and compare ROI scenarios with AI-powered projections. Connect to internal and external data sources
                  to generate accurate financial models for healthcare programs.
                </p>
              </div>
              <ROIModeler
                programId={selectedProgramId}
                programType={data.program_name.includes('ECM') ? 'CalAIM_ECM' : 'CalAIM_CS'}
                sponsorId={data.sponsor_id}
                initialConfig={{
                  enrollment_target: data.mcp_roi?.config?.enrollment_target || 1000,
                  total_budget: data.mcp_roi?.config?.total_budget || 500000,
                  avg_pmpm_rate: data.mcp_roi?.config?.avg_pmpm_rate || 500,
                  baseline_pmpm_cost: data.mcp_roi?.config?.baseline_pmpm_cost || 800,
                  target_pmpm_cost: data.mcp_roi?.config?.target_pmpm_cost || 600,
                  target_retention_rate_6mo: data.mcp_roi?.config?.target_retention_rate_6mo || 85,
                  target_retention_rate_12mo: data.mcp_roi?.config?.target_retention_rate_12mo || 75,
                }}
              />
            </div>
          )}

          {activeTab === 'pricing' && (
            <div className="space-y-6">
              {/* Pricing Configuration */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Settings className="w-5 h-5 text-purple-600" />
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t('admin.trust_pricing_configuration', 'Trust Pricing Configuration')}</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  {t('admin.adjust_pricing_parameters_to_model', 'Adjust pricing parameters to model profitability scenarios')}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Transaction Fee */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Transaction Fee (% of payout volume)
                    </label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="range"
                        min="0.5"
                        max="50"
                        step="0.5"
                        value={pricingConfig.transactionFeePercent}
                        onChange={(e) => setPricingConfig({
                          ...pricingConfig,
                          transactionFeePercent: parseFloat(e.target.value)
                        })}
                        className="flex-1"
                      />
                      <span className="text-lg font-bold text-purple-600 w-16">
                        {pricingConfig.transactionFeePercent.toFixed(1)}%
                      </span>
                    </div>
                  </div>

                  {/* Setup Fee */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('admin.setup_fee_onetime', 'Setup Fee (one-time)')}
                    </label>
                    <input
                      type="number"
                      value={pricingConfig.setupFee}
                      onChange={(e) => setPricingConfig({
                        ...pricingConfig,
                        setupFee: parseInt(e.target.value) || 0
                      })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Monthly Subscription */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('admin.monthly_subscription_fee', 'Monthly Subscription Fee')}
                    </label>
                    <input
                      type="number"
                      value={pricingConfig.subscriptionFeeMonthly}
                      onChange={(e) => setPricingConfig({
                        ...pricingConfig,
                        subscriptionFeeMonthly: parseInt(e.target.value) || 0
                      })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Analytics Fee */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Analytics Fee (% of volume)
                    </label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="range"
                        min="0"
                        max="2"
                        step="0.1"
                        value={pricingConfig.analyticsFeePercent}
                        onChange={(e) => setPricingConfig({
                          ...pricingConfig,
                          analyticsFeePercent: parseFloat(e.target.value)
                        })}
                        className="flex-1"
                      />
                      <span className="text-lg font-bold text-purple-600 w-16">
                        {pricingConfig.analyticsFeePercent.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Payment Processing Configuration */}
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase mb-4">
                    Payment Processing (Stripe, Adyen, Marqeta, etc.)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('admin.processing_fee', 'Processing Fee (%)')}
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          step="0.1"
                          value={pricingConfig.paymentProcessingFeePercent}
                          onChange={(e) => setPricingConfig({
                            ...pricingConfig,
                            paymentProcessingFeePercent: parseFloat(e.target.value) || 0
                          })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                        <span className="text-gray-500 dark:text-gray-400">%</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('admin.pertransaction_fee', 'Per-Transaction Fee')}
                      </label>
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500 dark:text-gray-400">$</span>
                        <input
                          type="number"
                          step="0.01"
                          value={pricingConfig.perTransactionFee}
                          onChange={(e) => setPricingConfig({
                            ...pricingConfig,
                            perTransactionFee: parseFloat(e.target.value) || 0
                          })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Chargeback to Sponsor (%)
                      </label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="5"
                          value={pricingConfig.chargebackPercent}
                          onChange={(e) => setPricingConfig({
                            ...pricingConfig,
                            chargebackPercent: parseInt(e.target.value)
                          })}
                          className="flex-1"
                        />
                        <span className="text-lg font-bold text-purple-600 w-12">
                          {pricingConfig.chargebackPercent}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Infrastructure & Operations Costs */}
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase mb-4">
                    Infrastructure & Operations Costs (Monthly)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('admin.cloud_hosting_1', 'Cloud Hosting')}
                      </label>
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500 dark:text-gray-400">$</span>
                        <input
                          type="number"
                          value={pricingConfig.cloudHostingCosts}
                          onChange={(e) => setPricingConfig({
                            ...pricingConfig,
                            cloudHostingCosts: parseFloat(e.target.value) || 0
                          })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('admin.database_storage_1', 'Database & Storage')}
                      </label>
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500 dark:text-gray-400">$</span>
                        <input
                          type="number"
                          value={pricingConfig.databaseStorageCosts}
                          onChange={(e) => setPricingConfig({
                            ...pricingConfig,
                            databaseStorageCosts: parseFloat(e.target.value) || 0
                          })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('admin.personnel_allocation', 'Personnel Allocation')}
                      </label>
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500 dark:text-gray-400">$</span>
                        <input
                          type="number"
                          value={pricingConfig.personnelCosts}
                          onChange={(e) => setPricingConfig({
                            ...pricingConfig,
                            personnelCosts: parseFloat(e.target.value) || 0
                          })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('admin.support_tools_1', 'Support Tools')}
                      </label>
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500 dark:text-gray-400">$</span>
                        <input
                          type="number"
                          value={pricingConfig.supportToolsCosts}
                          onChange={(e) => setPricingConfig({
                            ...pricingConfig,
                            supportToolsCosts: parseFloat(e.target.value) || 0
                          })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('admin.compliance_security_1', 'Compliance & Security')}
                      </label>
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500 dark:text-gray-400">$</span>
                        <input
                          type="number"
                          value={pricingConfig.complianceSecurityCosts}
                          onChange={(e) => setPricingConfig({
                            ...pricingConfig,
                            complianceSecurityCosts: parseFloat(e.target.value) || 0
                          })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('admin.marketing_sales_1', 'Marketing & Sales')}
                      </label>
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500 dark:text-gray-400">$</span>
                        <input
                          type="number"
                          value={pricingConfig.marketingSalesCosts}
                          onChange={(e) => setPricingConfig({
                            ...pricingConfig,
                            marketingSalesCosts: parseFloat(e.target.value) || 0
                          })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('admin.total_monthly_operations_cost', 'Total Monthly Operations Cost')}</span>
                      <span className="text-lg font-bold text-red-600">
                        {formatCurrency(
                          pricingConfig.cloudHostingCosts +
                          pricingConfig.databaseStorageCosts +
                          pricingConfig.personnelCosts +
                          pricingConfig.supportToolsCosts +
                          pricingConfig.complianceSecurityCosts +
                          pricingConfig.marketingSalesCosts
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-gray-500 dark:text-gray-400">{t('admin.annualized', 'Annualized')}</span>
                      <span className="text-sm font-semibold text-red-500 dark:text-red-400">
                        {formatCurrency(
                          (pricingConfig.cloudHostingCosts +
                          pricingConfig.databaseStorageCosts +
                          pricingConfig.personnelCosts +
                          pricingConfig.supportToolsCosts +
                          pricingConfig.complianceSecurityCosts +
                          pricingConfig.marketingSalesCosts) * 12
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Baseline Threshold Pricing */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border border-blue-200 dark:border-gray-700 rounded-lg p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t('admin.baseline_threshold_pricing', 'Baseline Threshold Pricing')}</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  {t('admin.model_pricing_based_on_improvement', 'Model pricing based on improvement over sponsor\'s baseline enrollment rate')}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Baseline Enrollment */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sponsor Baseline (without Trust)
                    </label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="range"
                        min="5"
                        max="50"
                        step="1"
                        value={pricingConfig.baselineEnrollmentPercent}
                        onChange={(e) => setPricingConfig({
                          ...pricingConfig,
                          baselineEnrollmentPercent: parseInt(e.target.value)
                        })}
                        className="flex-1"
                      />
                      <span className="text-lg font-bold text-blue-600 dark:text-blue-400 w-16">
                        {pricingConfig.baselineEnrollmentPercent}%
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('admin.enrollment_rate_sponsor_achieves_on', 'Enrollment rate sponsor achieves on their own')}
                    </p>
                  </div>

                  {/* Target Enrollment */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('admin.target_with_trust', 'Target with Trust')}
                    </label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="range"
                        min={pricingConfig.baselineEnrollmentPercent + 1}
                        max="80"
                        step="1"
                        value={pricingConfig.targetEnrollmentPercent}
                        onChange={(e) => setPricingConfig({
                          ...pricingConfig,
                          targetEnrollmentPercent: parseInt(e.target.value)
                        })}
                        className="flex-1"
                      />
                      <span className="text-lg font-bold text-green-600 dark:text-green-400 w-16">
                        {pricingConfig.targetEnrollmentPercent}%
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('admin.target_enrollment_rate_with_trust', 'Target enrollment rate with Trust platform')}
                    </p>
                  </div>
                </div>

                {/* Pricing Impact Analysis */}
                {pricingImpact && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-200 dark:border-gray-700">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{t('admin.value_attribution_analysis', 'Value Attribution Analysis')}</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.total_enrollments', 'Total Enrollments')}</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">
                          {formatNumber(pricingImpact.totalEnrollments)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.baseline_sponsor', 'Baseline (Sponsor)')}</p>
                        <p className="text-xl font-bold text-gray-500 dark:text-gray-400">
                          {formatNumber(pricingImpact.baselineEnrollments)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Trust-Attributable</p>
                        <p className="text-xl font-bold text-green-600 dark:text-green-400">
                          {formatNumber(pricingImpact.trustAttributableEnrollments)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.improvement', 'Improvement')}</p>
                        <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          +{pricingImpact.improvementPercent}%
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Trust Fee Revenue (on incremental value only)
                        </span>
                        <span className="text-xl font-bold text-purple-600">
                          {formatCurrency(pricingImpact.trustFeeRevenue)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Based on {pricingConfig.transactionFeePercent}% of {formatCurrency(pricingImpact.incrementalValue)} incremental value
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Payment Processing Cost Breakdown */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <CreditCard className="w-5 h-5 text-indigo-600" />
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t('admin.payment_processing_cost_breakdown', 'Payment Processing Cost Breakdown')}</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Cost Breakdown */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase">{t('admin.fee_structure', 'Fee Structure')}</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('admin.payout_volume', 'Payout Volume')}</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(processingBreakdown.totalVolume)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Percentage Fee ({processingBreakdown.processingFeePercent}%)
                        </span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(processingBreakdown.totalVolume * (pricingConfig.paymentProcessingFeePercent / 100))}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Per-Transaction Fee (${processingBreakdown.perTransactionFee} × {formatNumber(processingBreakdown.transactionCount)})
                        </span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(processingBreakdown.transactionCount * pricingConfig.perTransactionFee)}
                        </span>
                      </div>
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-2 flex justify-between">
                        <span className="text-sm font-bold text-gray-900 dark:text-white">{t('admin.total_processing_fees', 'Total Processing Fees')}</span>
                        <span className="text-lg font-bold text-red-600">
                          {formatCurrency(processingBreakdown.estimatedProcessingFees)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Chargeback Analysis */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase">{t('admin.cost_recovery', 'Cost Recovery')}</h4>
                    <div className="space-y-3">
                      <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm font-medium text-green-900 dark:text-green-300">{t('admin.chargeable_to_sponsor', 'Chargeable to Sponsor')}</p>
                            <p className="text-xs text-green-700 dark:text-green-400">{pricingConfig.chargebackPercent}% pass-through</p>
                          </div>
                          <span className="text-lg font-bold text-green-600 dark:text-green-400">
                            {formatCurrency(processingBreakdown.chargeableToSponsor)}
                          </span>
                        </div>
                      </div>
                      <div className="bg-orange-50 dark:bg-orange-900/30 rounded-lg p-3">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm font-medium text-orange-900 dark:text-orange-300">{t('admin.platform_absorbed', 'Platform Absorbed')}</p>
                            <p className="text-xs text-orange-700 dark:text-orange-400">{100 - pricingConfig.chargebackPercent}% cost of doing business</p>
                          </div>
                          <span className="text-lg font-bold text-orange-600 dark:text-orange-400">
                            {formatCurrency(processingBreakdown.platformAbsorbed)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <AlertCircle className="w-4 h-4 text-gray-500 dark:text-gray-400 mt-0.5" />
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          Processing fees calculated at {pricingConfig.paymentProcessingFeePercent}% + ${pricingConfig.perTransactionFee.toFixed(2)} per transaction.
                          Consider negotiating volume discounts for programs exceeding $1M/month.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Profitability Summary */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 border border-purple-200 dark:border-gray-700 rounded-lg p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Calculator className="w-5 h-5 text-purple-600" />
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t('admin.profitability_model_summary', 'Profitability Model Summary')}</h3>
                </div>

                {(() => {
                  const projectedRevenue = pricingConfig.setupFee +
                    (pricingConfig.subscriptionFeeMonthly * 12) +
                    (pricingImpact?.trustFeeRevenue || 0);
                  const annualOperationsCost = (
                    pricingConfig.cloudHostingCosts +
                    pricingConfig.databaseStorageCosts +
                    pricingConfig.personnelCosts +
                    pricingConfig.supportToolsCosts +
                    pricingConfig.complianceSecurityCosts +
                    pricingConfig.marketingSalesCosts
                  ) * 12;
                  const totalCosts = processingBreakdown.platformAbsorbed + annualOperationsCost;
                  const netProfit = projectedRevenue - totalCosts;
                  const profitMargin = projectedRevenue > 0 ? (netProfit / projectedRevenue) * 100 : 0;

                  return (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.projected_revenue', 'Projected Revenue')}</p>
                        <p className="text-xl font-bold text-purple-600">
                          {formatCurrency(projectedRevenue)}
                        </p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.operations_costs', 'Operations Costs')}</p>
                        <p className="text-xl font-bold text-red-600 dark:text-red-400">
                          {formatCurrency(annualOperationsCost)}
                        </p>
                        <p className="text-xs text-gray-400">{t('admin.annual', 'Annual')}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.processing_net', 'Processing (Net)')}</p>
                        <p className="text-xl font-bold text-red-600 dark:text-red-400">
                          {formatCurrency(processingBreakdown.platformAbsorbed)}
                        </p>
                        <p className="text-xs text-gray-400">{100 - pricingConfig.chargebackPercent}% absorbed</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.net_profit', 'Net Profit')}</p>
                        <p className={`text-xl font-bold ${netProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {formatCurrency(netProfit)}
                        </p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.profit_margin', 'Profit Margin')}</p>
                        <p className={`text-xl font-bold ${profitMargin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {formatPercent(profitMargin)}
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminROIDashboard;
